// Static reference content for the 6 life pillars.
// Ids are used in routes: /pillar/:id
export const SELF_EXPRESSION_LINE =
  "Self-Expression — the unifying purpose behind every pillar.";

export const PILLARS = [
  {
    id: "home",
    name: "Home",
    icon: "Home",
    color: "#A8C3A0",
    colorName: "Soft Sage",
    tagline: "My physical foundation, where I plant roots.",
    anchor: "Move toward a settled, deliberate living situation.",
    definition:
      "Home is the physical foundation of my life — the place where I plant roots and feel grounded enough to express everything else.",
    whatGood: [
      "A calm, pet-friendly space that feels genuinely mine.",
      "Housing decisions made deliberately, not reactively.",
      "Roots that support the other pillars rather than drain them.",
    ],
    goals: [
      "Find a rental (pet-friendly, quiet, affordable, Northeast Netherlands) within 3 months.",
      "Rent deliberately to build savings and let the housing market cool.",
      "Buy once finances and the market align.",
    ],
    crossLinks: [
      { label: "Finance funds Home", to: "/pillar/finance" },
    ],
  },
  {
    id: "finance",
    name: "Finance",
    icon: "Wallet",
    color: "#9DB7C5",
    colorName: "Dusty Blue",
    tagline: "The means to live well now while building deliberately for the future.",
    anchor: "Keep saving steadily toward the €100k milestone.",
    definition:
      "Finance is the means to live the life I want now while building deliberately for the future — moving away from short-term thinking.",
    whatGood: [
      "Spending that reflects my values, not impulse.",
      "Savings growing on a deliberate, long-term arc.",
      "Money serving the other pillars, especially Home.",
    ],
    goals: [
      "~€50,000 saved today.",
      "Reach €100,000 within 5 years.",
      "Fund the Home pillar: down payment and home investments.",
    ],
    crossLinks: [
      { label: "Finance funds Home", to: "/pillar/home" },
    ],
  },
  {
    id: "work",
    name: "Work",
    icon: "Briefcase",
    color: "#B7A9C8",
    colorName: "Soft Lavender",
    tagline: "A place to express intelligence and ability, and build lasting roots.",
    anchor: "Contribute meaningfully and protect long-term growth.",
    definition:
      "Work is a place to express intelligence and ability, help others and the company grow, and build the financial foundation for the other pillars. It is meant as long-term roots, not a stepping stone.",
    whatGood: [
      "Work that expresses real intelligence and ability.",
      "Helping others and the company grow together.",
      "Sustainable energy, not burnout.",
    ],
    goals: [
      "Treat work as long-term roots, not a stepping stone.",
      "Build the financial foundation for the other pillars.",
      "Manage energy so work stays sustainable.",
    ],
    crossLinks: [
      { label: "Health fuels Work", to: "/pillar/health" },
    ],
  },
  {
    id: "health",
    name: "Health",
    icon: "HeartPulse",
    color: "#8FB9B2",
    colorName: "Muted Teal",
    tagline: "The fuel that powers self-expression across every other pillar.",
    anchor: "Balance discipline with rest and good food.",
    definition:
      "Health is the fuel that powers self-expression across every other pillar. It is also about self-acceptance — accepting the need for rest and good food, alongside discipline.",
    whatGood: [
      "Discipline balanced with genuine rest.",
      "Eating that feels nourishing, not punishing.",
      "Accepting the body's needs instead of fighting them.",
    ],
    goals: [
      "Body fat ~15% (not weight-focused).",
      "Mediterranean-style, lower-carb, unsaturated-fat-focused eating.",
      "Accept rest and imperfection as part of the plan.",
    ],
    crossLinks: [
      { label: "Health fuels Work", to: "/pillar/work" },
    ],
  },
  {
    id: "family",
    name: "Family",
    icon: "Users",
    color: "#D6C3A5",
    colorName: "Warm Sand",
    tagline: "A pillar with real growth potential — learning to show myself fully.",
    anchor: "Do instead of just wanting; show up as myself.",
    definition:
      "Family is a pillar where I currently feel limited in fully showing myself — and where there is real growth potential. The focus is on birth family: parents, sister, and nephews.",
    whatGood: [
      "Feeling free to act, not just wanting to.",
      "Accepting family as they are.",
      "Discovering what 'being myself' actually means here.",
    ],
    goals: [
      "Feel free: do instead of just wanting.",
      "Find out what 'being myself' actually means.",
      "Stay close to parents, sister, and nephews.",
    ],
    crossLinks: [
      { label: "Acceptance is a shared thread", to: "/pillar/friends" },
    ],
  },
  {
    id: "friends",
    name: "Friends",
    icon: "Handshake",
    color: "#CFA6AA",
    colorName: "Soft Rose",
    tagline: "Fun and acceptance — a space of 'Being', without judgment.",
    anchor: "One real conversation or meetup this week.",
    definition:
      "Friends are about fun and acceptance — a space of 'Being', without judgment, where I don't hold back.",
    whatGood: [
      "Depth over frequency.",
      "Space where I don't hold back.",
      "Being fully accepted, and accepting in return.",
    ],
    goals: [
      "At least one real conversation or meetup with a close friend per week.",
      "Prioritise depth over frequency.",
      "Let friendship be a space of 'Being'.",
    ],
    crossLinks: [
      { label: "Acceptance is a shared thread", to: "/pillar/family" },
    ],
  },
];

export const PILLAR_NAMES = PILLARS.map((p) => p.name);

export function getPillarById(id) {
  return PILLARS.find((p) => p.id === id);
}

export function getPillarByName(name) {
  return PILLARS.find((p) => p.name === name);
}
